import ApiCall from "./ApiCall";

const STORAGE_KEY = "auth_page_session";

// A visitor who comes back after this long is treated as a new visit rather
// than a continuation of the old one, so the earlier abandonment still reads
// as a bounce.
const SESSION_TTL_MS = 30 * 60 * 1000;

// React 18's StrictMode double-invokes effects in development. The backend
// de-dupes on the session key too, but skipping the second request keeps the
// network tab honest.
let inFlight = false;

const newSessionKey = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback for browsers without crypto.randomUUID. The value only needs to be
  // unique enough to correlate one visit with one login, not cryptographically
  // strong — and the backend validates the UUID shape.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const readSession = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed?.key || !parsed?.createdAt) return null;
    if (Date.now() - parsed.createdAt >= SESSION_TTL_MS) return null;

    return parsed;
  } catch {
    return null;
  }
};

/**
 * Record a visit to the Login / Registration page.
 *
 * The view is stored server-side as a bounce and upgraded to a login or a
 * registration when the visitor converts, so the three outcomes stay mutually
 * exclusive. Called once when the page mounts.
 */
export const trackAuthPageView = async () => {
  try {
    // A live session means this visit is already counted — a refresh or a
    // back-navigation must not create a second view.
    if (readSession()) return;
    if (inFlight) return;

    inFlight = true;

    const key = newSessionKey();
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ key, createdAt: Date.now() }),
    );

    await ApiCall("POST", "auth-page-views/track", { session_key: key });
  } catch (error) {
    // Tracking must never block someone from logging in.
    console.error("Auth page view tracking failed:", error);
  } finally {
    inFlight = false;
  }
};

/**
 * The current visit's session key, sent with login and register calls so the
 * view can be attributed. Null when there is no live session.
 */
export const getAuthPageSessionKey = () => readSession()?.key || null;

/**
 * Clear the session once it has been attributed, so the next visit to the page
 * is counted as a fresh view.
 */
export const clearAuthPageSession = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};
